import React, { isValidElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { CircularProgress, Tooltip } from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
  GridToolbarContainer,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { randomId } from '@mui/x-data-grid-generator';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { CreateReconsignmentContext } from '../Create';
import {
  useGetDestinationsQuery,
  useGetFromLocationListQuery,
} from '../../reconsignmentsEndpoints';
import getStatusOptions from './GridPanelUtils';

const createDefaultReconsignmentRow = () => ({
  consignmentStatus: 'UNCONTROL',
  fromLocationId: null,
  id: randomId(),
  inventoryStatus: 'ALL',
  isNew: true,
  productStatus: 'UNRESERVED',
  toLocationId: null,
  volume: '',
});

/**
 * EditToolbar component provides an "Add 1 to 1 Reconsignment" button in the grid toolbar.
 *
 * @param {Object} props - Component props
 * @param {Function} props.setRows - Function to update the grid rows state
 * @param {Function} props.setRowModesModel - Function to update the row modes model state
 * @param {boolean} [props.disableAction=false] - When true, hides the "Add" button
 * @returns {JSX.Element} Toolbar component with add button
 */
function EditToolbar(props) {
  const { setRows, setRowModesModel } = props;

  const handleClick = () => {
    const newRow = createDefaultReconsignmentRow();
    setRows(oldRows => [...oldRows, newRow]);
    setRowModesModel(oldModel => ({
      ...oldModel,
      [newRow.id]: { fieldToFocus: 'fromLocationId', mode: GridRowModes.Edit },
    }));
  };

  return (
    <GridToolbarContainer>
      {!props.disableAction && (
        <Button
          color="primary"
          id="AddReconsignment"
          onClick={handleClick}
          startIcon={<AddIcon />}
          variant="outlined"
        >
          Add 1 to 1 Reconsignment
        </Button>
      )}
    </GridToolbarContainer>
  );
}

/**
 * AutocompleteEditCell provides a dropdown selection component for editable cells in the grid.
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Row ID for the cell
 * @param {any} props.value - Current value of the cell
 * @param {string} props.field - Field name/column ID
 * @param {Array<Object>} props.options - Options for the dropdown, each with label and value properties
 * @param {boolean} props.disableClearable - When true, prevents clearing the selection
 * @param {boolean} props.multiple - When true, allows multiple selections
 * @param {boolean} props.freeSolo - When true, allows free text input
 * @returns {JSX.Element} Autocomplete component for cell editing
 */
export function AutocompleteEditCell({
  id,
  value,
  field,
  options,
  disableClearable,
  multiple,
  freeSolo,
  disabled,
  onValueChange,
  renderInput,
}) {
  const apiRef = useGridApiContext();
  const defaultRenderInput = params => <TextField {...params} tabIndex={-1} />;

  const handleValueChange = (_, newValue) => {
    apiRef.current.setEditCellValue({ field, id, value: newValue || null });
    if (field === 'fromLocationId') {
      apiRef.current.setEditCellValue({ field: 'toLocationId', id, value: null });
    }
    if (onValueChange) {
      onValueChange(newValue || null);
    }
  };

  return (
    <Autocomplete
      autoHighlight
      disableClearable={disableClearable}
      disabled={disabled}
      freeSolo={freeSolo}
      fullWidth
      getOptionLabel={option => option.label}
      multiple={multiple}
      onChange={handleValueChange}
      options={options}
      renderInput={params => {
        const customInput = typeof renderInput === 'function' ? renderInput(params) : null;
        return isValidElement(customInput) ? customInput : defaultRenderInput(params);
      }}
      slotProps={{
        listbox: {
          onWheel: event => {
            event.stopPropagation();
          },
        },
      }}
      tabIndex={-1}
      value={options.find(o => o.value === value) || null}
    />
  );
}

/**
 * GridPanel is a configurable data grid component for displaying, editing, and managing tabular data.
 * It provides row actions, inline editing, keyboard navigation, and custom toolbars.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} [props.columns] - Custom column definitions
 * @param {boolean} [props.disableAction=false] - When true, disables row editing and toolbar actions
 * @param {Array<Object>} props.rows - Row data to display in the grid
 * @param {Function} props.setRows - Function to update the rows state
 * @param {Function} [props.toolbar] - Custom toolbar component
 * @param {boolean} [props.loading=false] - When true, shows loading spinner
 * @param {Function} [props.getRowId] - Function to extract row ID from row data
 * @param {string} [props.editMode='row'] - Edit mode, either 'row' or 'cell'
 * @param {Function} [props.processRowUpdate] - Custom function to process row updates
 * @param {Function} [props.setCanContinueWithBulk=()=>{}] - Callback to determine if bulk actions can continue
 * @returns {JSX.Element} Data grid component
 */
export default function GridPanel({
  columns,
  disableAction,
  rows,
  setRows,
  toolbar: CustomToolbar,
  loading,
  getRowId,
  editMode = 'row',
  processRowUpdate: customProcessRowUpdate,
  setCanContinueWithBulk,
}) {
  const { info } = useContext(CreateReconsignmentContext);
  const { product, rvp } = info;
  const productId = R.prop('productId', product);
  const { currentAffiliate } = useUserRolePermissions();
  const autoAddedProductIdRef = useRef(null);
  const [selectedFromLocationId, setSelectedFromLocationId] = useState(null);
  const affiliateParams = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const fromLocationParams = {
    ...affiliateParams,
    productId,
    ...(rvp ? { rvp } : {}),
  };

  const { data: fromLocationQuery = [], error: fromLocationError } = useGetFromLocationListQuery(
    { params: fromLocationParams },
    { skip: R.or(R.isNil(currentAffiliate), R.isNil(product)) }
  );

  const destinationParams = {
    ...affiliateParams,
    productId,
    ...(R.isNil(selectedFromLocationId) ? {} : { LocationId: selectedFromLocationId }),
  };

  const shouldSkipDestinationsQuery =
    R.isNil(currentAffiliate) || R.isNil(product) || R.isNil(selectedFromLocationId);

  const { data: productDestinations = [] } = useGetDestinationsQuery(
    { params: destinationParams },
    { skip: shouldSkipDestinationsQuery }
  );

  const [toLocationOptions, setToLocationOptions] = useState([]);
  const [currentFromLocationIndex, setCurrentFromLocationIndex] = useState();

  useEffect(() => {
    if (productDestinations.length === 0 || R.isNil(currentFromLocationIndex)) return;

    setToLocationOptions(prevOptions => {
      const nextOptions = [...prevOptions];
      nextOptions[currentFromLocationIndex] = productDestinations;
      return nextOptions;
    });
  }, [productDestinations, currentFromLocationIndex]);

  const apiRef = useGridApiRef();

  useEffect(() => {
    const rowIds = apiRef.current.getAllRowIds();
    rowIds.forEach(id => {
      apiRef.current.setEditCellValue({ field: 'toLocationId', id, value: null });
      apiRef.current.setEditCellValue({ field: 'fromLocationId', id, value: null });
    });
  }, [info, apiRef]);

  const [rowModesModel, setRowModesModel] = useState({});

  useEffect(() => {
    if (R.isNil(productId)) {
      autoAddedProductIdRef.current = null;
      return;
    }

    if (disableAction || rows.length > 0 || autoAddedProductIdRef.current === productId) return;

    const newRow = createDefaultReconsignmentRow();
    autoAddedProductIdRef.current = productId;
    setRows([newRow]);
    setRowModesModel({
      [newRow.id]: { fieldToFocus: 'fromLocationId', mode: GridRowModes.Edit },
    });
  }, [disableAction, productId, rows.length, setRows]);

  /**
   * Handles click on delete button for a row.
   *
   * @param {string|number} id - ID of row to delete
   * @returns {Function} Click handler that removes the row from state
   */

  const handleDeleteClick = id => () => {
    setRows(rows.filter(row => row.id !== id));
  };
  /**
   * Handles click on cancel button when editing a row.
   *
   * @param {string|number} id - ID of row being edited
   * @returns {Function} Click handler that cancels editing and restores original row
   */
  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { ignoreModifications: true, mode: GridRowModes.View },
    });

    const editedRow = rows.find(row => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  /**
   * Handles click on cancel button when editing a row.
   *
   * @param {string|number} id - ID of row being edited
   * @returns {Function} Click handler that cancels editing and restores original row
   */
  const defaultProcessRowUpdate = newRow => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map(row => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  /**
   * Handles changes to the row modes model.
   *
   * @param {Object} newRowModesModel - Updated row modes model
   */
  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  /**
   * Handles click on save button when editing a row.
   *
   * @param {string|number} id - ID of row being edited
   * @returns {Function} Click handler that saves changes and exits edit mode
   */
  const handleSaveClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  /**
   * Handles click on edit button for a row.
   *
   * @param {string|number} id - ID of row to edit
   * @returns {Function} Click handler that puts the row into edit mode
   */
  const handleEditClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  useEffect(() => {
    setCanContinueWithBulk(rows.filter(r => Object.keys(rowModesModel).includes(r.id)).length < 1);
  }, [rowModesModel, setCanContinueWithBulk, rows]);

  const editableColumn = !disableAction;

  const gridColumns = columns || [
    {
      editable: editableColumn,
      field: 'fromLocationId',
      headerName: 'From Location',
      renderEditCell: params => {
        return (
          <AutocompleteEditCell
            {...params}
            disableClearable
            freeSolo={false}
            multiple={false}
            onValueChange={newValue => {
              setSelectedFromLocationId(R.propOr(newValue, 'value', newValue));
              const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
              setCurrentFromLocationIndex(index);
            }}
            options={
              fromLocationError
                ? []
                : fromLocationQuery.map(l => ({ label: l.locationName, value: l.locationId }))
            }
            renderInput={values =>
              fromLocationQuery.length === 0 ||
              (fromLocationError && (
                <TextField
                  {...values}
                  slotProps={{
                    htmlInput: { ...values.inputProps },

                    input: {
                      ...values.InputProps,
                      endAdornment: (
                        <>
                          <Tooltip title="No options available">
                            <InfoRoundedIcon
                              sx={{
                                color: 'graphic.main',
                                position: 'absolute',
                                right: 12,
                                top: 14,
                              }}
                            />
                          </Tooltip>
                        </>
                      ),
                    },
                  }}
                />
              ))
            }
            // eslint-disable-next-line react/prop-types
            value={params.value?.value}
          />
        );
      },
      sortable: false,
      valueFormatter: params => {
        // eslint-disable-next-line react/prop-types
        return params.value?.label;
      },
      width: 200,
    },
    {
      editable: editableColumn,
      field: 'toLocationId',
      headerName: 'To Location',
      renderEditCell: params => {
        const fromLocation = R.path(['row', 'fromLocationId'], params);
        const fromLocationId = R.propOr(fromLocation, 'value', fromLocation);
        const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <AutocompleteEditCell
            {...params}
            disableClearable
            disabled={R.isNil(fromLocationId)}
            freeSolo={false}
            multiple={false}
            options={
              toLocationOptions[index]?.map(l => ({
                label: l.locationName,
                value: l.locationId,
              })) || []
            }
            // eslint-disable-next-line react/prop-types
            value={params.value?.value}
          />
        );
      },
      sortable: false,
      valueFormatter: params => {
        // eslint-disable-next-line react/prop-types
        return params.value?.label;
      },
      width: 200,
    },
    {
      editable: editableColumn,
      field: 'volume',
      headerName: 'Transfer Volume',
      sortable: false,
      width: 120,
    },
    {
      editable: editableColumn,
      field: 'inventoryStatus',
      headerName: 'Inventory Status',
      sortable: false,
      type: 'singleSelect',
      valueOptions: getStatusOptions('inventory'),
      width: 200,
    },
    {
      editable: editableColumn,
      field: 'consignmentStatus',
      headerName: 'Reconsignment Controlled',
      sortable: false,
      type: 'singleSelect',
      valueOptions: getStatusOptions('consignment'),
      width: 200,
    },
    {
      cellClassName: 'actions',
      field: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (disableAction) return [];

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Confirm"
              onClick={handleSaveClick(id)}
              sx={{
                color: 'primary.main',
              }}
            />,
            <GridActionsCellItem
              className="textPrimary"
              color="inherit"
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            className="textPrimary"
            color="inherit"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
          />,
          <GridActionsCellItem
            className="textPrimary"
            color="inherit"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
      headerName: 'Actions',
      type: 'actions',
      width: 300,
    },
  ];

  /**
   * Handles stopping row edit mode based on keyboard events.
   * Provides advanced keyboard navigation between cells and rows.
   *
   * @param {Object} curr - Current edit stop event details
   * @param {Object} event - Keyboard event that triggered edit stop
   */
  const handleRowEditStop = (curr, event) => {
    const idx = rows.reduce((a, c, i) => (c.id === curr.id ? i : a), null);
    const prev = rows[idx - 1] || null;
    const next = rows[idx + 1] || null;

    // if we press enter in a complex component, set the val and move to the next col
    if (event.key === 'Enter') {
      if (curr.reason === 'enterKeyDown') {
        const currCol = columns.find(c => c.field === curr.field);
        const colIdx = columns.indexOf(currCol);

        if (colIdx < 0) return;

        // eslint-disable-next-line no-param-reassign
        event.defaultMuiPrevented = true;

        const input = apiRef.current
          .getCellElement(curr.id, columns[colIdx + 1].field)
          .getElementsByTagName('input')[0];

        if (input) {
          input.focus();
        } else {
          apiRef.current.getCellElement(curr.id, columns[colIdx + 1].field).focus();
        }
        return;
      }
    }
    // If we are tabbing out of a row, let's save and go to the next cell
    if (event.key === 'Tab') {
      const currRow = rows.find(r => r.id === curr.row.id);
      const rowIdx = rows.indexOf(currRow);

      if (curr.reason === GridRowEditStopReasons.shiftTabKeyDown && prev) {
        apiRef.current.getCellElement(prev.id, columns.findLast(c => c.editable).field).focus();
        return;
      }
      if (curr.reason === GridRowEditStopReasons.tabKeyDown && next) {
        apiRef.current.getCellElement(next.id, columns[0].field).focus();
        return;
      }
      if (curr.reason === GridRowEditStopReasons.tabKeyDown && !next) {
        // if we are in the last row, we need to save that row and exit
        if (rowIdx === rows.length - 1) {
          setRowModesModel({ ...rowModesModel, [curr.id]: { mode: GridRowModes.View } });
          return;
        }
      }
    }

    // eslint-disable-next-line no-param-reassign
    event.defaultMuiPrevented = true;
  };

  /**
   * Returns spacing configuration for grid rows.
   *
   * @param {Object} rowSpacingParams - Row spacing parameters
   * @param {boolean} rowSpacingParams.isLastVisible - Whether this is the last visible row
   * @returns {Object} Spacing configuration for top and bottom of row
   */
  const getRowSpacing = useCallback(rowSpacingParams => {
    return {
      bottom: rowSpacingParams.isLastVisible ? 0 : 2,
      top: 0,
    };
  }, []);

  const ToolbarComponent = CustomToolbar || EditToolbar;

  return (
    <Box
      sx={{
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        height: 'calc(100vh - 220px)',
        pb: 0,
        width: '100%',
      }}
    >
      {loading ? (
        <Box
          sx={{
            p: theme => ({ lg: theme.spacing(4, 1, 1), xs: theme.spacing(9, 3, 4) }),
            textAlign: 'center',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <DataGridPremium
          apiRef={apiRef}
          columns={gridColumns}
          disableColumnMenu
          editMode={editMode}
          getRowId={getRowId || (row => row.id)}
          getRowSpacing={getRowSpacing}
          hideFooterPagination
          onRowEditStop={handleRowEditStop}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={customProcessRowUpdate || defaultProcessRowUpdate}
          rowModesModel={rowModesModel}
          rows={rows}
          slotProps={{
            toolbar: { disableAction, setRowModesModel, setRows },
          }}
          slots={{
            toolbar: ToolbarComponent,
          }}
        />
      )}
    </Box>
  );
}

EditToolbar.propTypes = {
  disableAction: bool,
  setRowModesModel: func.isRequired,
  setRows: func.isRequired,
};

EditToolbar.defaultProps = {
  disableAction: false,
};

AutocompleteEditCell.propTypes = {
  disableClearable: bool.isRequired,
  disabled: bool,
  field: string.isRequired,
  freeSolo: bool.isRequired,
  id: string.isRequired,
  multiple: bool.isRequired,
  onValueChange: func,
  options: arrayOf(shape({})).isRequired,
  renderInput: func,
  value: number,
};
AutocompleteEditCell.defaultProps = {
  disabled: false,
  onValueChange: null,
  renderInput: null,
  value: null,
};

Autocomplete.propTypes = {
  value: shape({
    label: string.isRequired,
    value: number.isRequired,
  }),
};
Autocomplete.defaultProps = {
  value: null,
};

GridPanel.propTypes = {
  columns: arrayOf(shape({})),
  disableAction: bool,
  editMode: string,
  getRowId: func,
  loading: bool,
  processRowUpdate: func,
  rows: arrayOf(shape({})).isRequired,
  setCanContinueWithBulk: func,
  setRows: func.isRequired,
  toolbar: func,
};

GridPanel.defaultProps = {
  columns: null,
  disableAction: false,
  editMode: 'row',
  getRowId: row => row.id,
  loading: false,
  processRowUpdate: null,
  setCanContinueWithBulk: () => {},
  toolbar: null,
};
